import { Divider, Group } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Task } from '../../../api/tasks-client/task-client.type';
import { useTaskSearch } from '../../../api/tasks-client/tasks-client.hooks';
import { useProcessStore, useTaskSearchHistory } from '../../../store/process-store/process-store.hooks';
import { secondaryBase } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { TaskSearchCombobox } from './task-search-combobox';
import type { TaskSearchComboboxRef } from './task-search-combobox';
import { getTaskOptionLabel } from './task-select.helpers';

type TaskSelectProps = {
  hideLabel?: boolean;
  handleTaskSelected?: (task: Task | null) => void;
  isRightSide?: boolean;
  startDate?: string | null;
  endDate?: string | null;
};

type TaskSelectRef = {
  clearSelection: () => void;
};

/**
 * Searchable Task field of the Process filter panel.
 *
 * Runs the scope-filtered task search for the typed text and, through the shared
 * `TaskSearchCombobox`, lists the portfolio's recent picks above the results. A
 * pick writes the task into the left or right process scope exactly as the
 * previous `Select`-based field did, notifies the parent, and records the task
 * at the top of the recent list.
 *
 * @param {TaskSelectProps} props - Which side's scope to drive, the date range for the search, and the pick callback
 * @param {React.Ref<TaskSelectRef>} ref - Imperative handle exposing `clearSelection`
 * @returns {JSX.Element} The labelled field between its dividers
 */
export const TaskSelect = forwardRef<TaskSelectRef, TaskSelectProps>(
  ({ hideLabel, handleTaskSelected, isRightSide = false, startDate, endDate }, ref) => {
    const { t } = useTranslation();
    const { scope: leftScope, setScope: setLeftScope, rightScope, setRightScope } = useProcessStore();
    const { entries, addEntry, removeEntry, clearEntries } = useTaskSearchHistory();
    const comboboxRef = useRef<TaskSearchComboboxRef>(null);

    const scope = isRightSide ? rightScope : leftScope;
    const { team, board, workflow, task, taskTypes } = scope;

    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchValue, 300);

    const taskTypeString = taskTypes?.length ? taskTypes.join(',') : null;

    const { tasks } = useTaskSearch(
      debouncedSearch,
      team?.id,
      board?.id,
      workflow?.id,
      taskTypeString,
      startDate,
      endDate,
      {
        enabled: Boolean(debouncedSearch && (team?.id || board?.id || workflow?.id)),
        queryKey: [
          'tasks',
          debouncedSearch,
          team?.id,
          board?.id,
          workflow?.id,
          taskTypeString,
          startDate,
          endDate,
          isRightSide ? 'right' : 'left',
        ],
      },
    );

    const applyTask = useCallback(
      (picked: Task) => {
        if (isRightSide) {
          setRightScope({ task: picked });
        } else {
          setLeftScope({ task: picked });
        }
        handleTaskSelected?.(picked);
        addEntry(picked);
      },
      [addEntry, handleTaskSelected, isRightSide, setLeftScope, setRightScope],
    );

    const clearSelection = useCallback(() => {
      // Empties typed text too, so the field is blank even when no task was selected.
      comboboxRef.current?.reset();
      setSearchValue('');

      if (isRightSide) {
        setRightScope({ task: null });
      } else {
        setLeftScope({ task: null });
      }
      handleTaskSelected?.(null);
    }, [handleTaskSelected, isRightSide, setLeftScope, setRightScope]);

    useImperativeHandle(ref, () => ({ clearSelection }), [clearSelection]);

    const removeRecent = useCallback((recent: Task) => removeEntry(recent.id), [removeEntry]);

    const taskString = `${t('common.work_unit')}`;
    const fieldName = isRightSide ? `${taskString} search (right graph)` : `${taskString} search`;

    return (
      <div style={{ marginTop: '30px' }}>
        <Divider style={{ marginBottom: '15px' }} />
        {!hideLabel && (
          <Group style={{ marginBottom: '15px' }} gap={5}>
            <Text size="small" weight="medium">
              {taskString}
            </Text>
            <Tooltip
              label={`Search for a ${taskString}. Type in the ${taskString.toLocaleLowerCase()} name or URL. Limit of 10 results based on the search query.`}
              variant="black"
              multiline
              maw={300}
            >
              <Icon name="info" size={16} color={secondaryBase} />
            </Tooltip>
          </Group>
        )}
        <TaskSearchCombobox
          ref={comboboxRef}
          selectedLabel={task ? getTaskOptionLabel(task) : ''}
          selectedTaskId={task?.id ?? null}
          results={tasks}
          recentEntries={entries}
          onSearchChange={setSearchValue}
          onPick={applyTask}
          onRemoveRecent={removeRecent}
          onClearRecent={clearEntries}
          onClear={clearSelection}
          fieldName={fieldName}
          placeholder={`Type to search for a ${taskString}`}
          listName={`${taskString} suggestions`}
        />
        <Divider style={{ marginTop: '30px' }} />
      </div>
    );
  },
);
